import React, { Component, createRef } from 'react';
import { Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import helper from '../../utils/helper';
import Action from '../../utils/operationsActions';
import { getActionIcon } from './mainLayoutGroups';

interface AccessRoute {
    path: string;
    actions?: any[];
}

interface SearchHit {
    actionName: string;
    path: string;
    name: string;
    description: string;
    category: string;
}

interface Props extends RouteComponentProps {
    accessRoutes?: AccessRoute[];
}

interface State {
    query: string;
    open: boolean;
    activeIndex: number;
}

const MAX_RESULTS = 8;

const actionKey = (raw: any): string => {
    if (!raw) return '';
    if (typeof raw === 'string') return raw.trim();
    if (typeof raw === 'object' && raw.actionName) return String(raw.actionName).trim();
    return '';
};

const routeActions = (route: AccessRoute): string[] => {
    const raw = route.actions;
    if (Array.isArray(raw)) return raw.map(actionKey).filter(Boolean);
    if (typeof raw === 'string') return raw.split(',').map((item) => item.trim()).filter(Boolean);
    return [];
};

export class GlobalSearch extends Component<Props, State> {
    state: State = { query: '', open: false, activeIndex: 0 };
    wrapRef = createRef<HTMLDivElement>();
    inputRef = createRef<any>();

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        document.addEventListener('keydown', this.handleGlobalKey);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        document.removeEventListener('keydown', this.handleGlobalKey);
    }

    handleGlobalKey = (event: KeyboardEvent) => {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
            event.preventDefault();
            const input = this.inputRef.current;
            if (input?.input?.focus) {
                input.input.focus();
            } else if (input?.focus) {
                input.focus();
            }
            this.setState({ open: true });
        }
    };

    handleClickOutside = (event: MouseEvent) => {
        if (this.wrapRef.current && !this.wrapRef.current.contains(event.target as Node)) {
            this.setState({ open: false });
        }
    };

    getCatalog = () => {
        try {
            return Action.actionsCreated() || [];
        } catch (e) {
            return [];
        }
    };

    getIndex = (): SearchHit[] => {
        const catalog = this.getCatalog();
        const routes = Array.isArray(this.props.accessRoutes) ? this.props.accessRoutes : [];
        const hits: SearchHit[] = [];

        routes.forEach((route) => {
            if (!route?.path) return;
            routeActions(route).forEach((name) => {
                const meta = catalog.find((action: { actionName: string }) => action.actionName === name);
                hits.push({
                    actionName: name,
                    path: route.path,
                    name: meta?.name || helper.unCamelCase(name),
                    description: meta?.description || '',
                    category: helper.unCamelCase(route.path),
                });
            });
        });

        return hits;
    };

    getResults = (): SearchHit[] => {
        const query = this.state.query.trim().toLowerCase();
        if (!query) return [];

        return this.getIndex()
            .map((hit) => {
                const haystack = `${hit.name} ${hit.description} ${hit.actionName} ${hit.category}`.toLowerCase();
                const nameMatch = hit.name.toLowerCase().includes(query);
                const starts = hit.name.toLowerCase().startsWith(query) || hit.actionName.toLowerCase().startsWith(query);
                return { hit, haystack, nameMatch, starts };
            })
            .filter((row) => row.haystack.includes(query))
            .sort((a, b) => Number(b.starts) - Number(a.starts) || Number(b.nameMatch) - Number(a.nameMatch))
            .slice(0, MAX_RESULTS)
            .map((row) => row.hit);
    };

    openAction = (hit: SearchHit) => {
        this.props.history.push(`/${hit.path}/${hit.actionName}`);
        this.setState({ query: '', open: false, activeIndex: 0 });
    };

    handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ query: event.target.value, open: true, activeIndex: 0 });
    };

    handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const results = this.getResults();
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.setState((prev) => ({
                open: true,
                activeIndex: results.length ? (prev.activeIndex + 1) % results.length : 0,
            }));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.setState((prev) => ({
                open: true,
                activeIndex: results.length ? (prev.activeIndex - 1 + results.length) % results.length : 0,
            }));
            return;
        }
        if (event.key === 'Enter') {
            const hit = results[this.state.activeIndex] || results[0];
            if (hit) {
                event.preventDefault();
                this.openAction(hit);
            }
            return;
        }
        if (event.key === 'Escape') {
            this.setState({ open: false });
        }
    };

    render() {
        const { query, open, activeIndex } = this.state;
        const results = this.getResults();
        const showPanel = open && query.trim().length > 0;

        return (
            <div className="appNav-search" ref={this.wrapRef}>
                <Input
                    ref={this.inputRef}
                    allowClear
                    value={query}
                    onChange={this.handleChange}
                    onFocus={() => this.setState({ open: true })}
                    onKeyDown={this.handleKeyDown}
                    prefix={<SearchOutlined />}
                    placeholder="Search actions"
                    aria-label="Search actions"
                    className="appNav-searchInput"
                />
                {showPanel && (
                    <div className="appNav-searchPanel" role="listbox">
                        {results.length === 0 ? (
                            <div className="appNav-searchEmpty">No actions match “{query.trim()}”</div>
                        ) : results.map((hit, index) => (
                            <button
                                type="button"
                                key={`${hit.path}-${hit.actionName}`}
                                className={`appNav-searchHit${index === activeIndex ? ' is-active' : ''}`}
                                onMouseEnter={() => this.setState({ activeIndex: index })}
                                onClick={() => this.openAction(hit)}
                                role="option"
                                aria-selected={index === activeIndex}
                            >
                                <span className="appNav-searchHit__icon">
                                    {getActionIcon(hit.actionName, hit.name)}
                                </span>
                                <span className="appNav-searchHit__copy">
                                    <span className="appNav-searchHit__name">{hit.name}</span>
                                    <span className="appNav-searchHit__meta">
                                        {hit.category}
                                        {hit.description ? ` · ${hit.description.replace(/^Click to /i, '')}` : ''}
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = (state: any) => ({
    accessRoutes: state.auth.accessRoutes,
});

export default withRouter(connect(mapStateToProps)(GlobalSearch));
